import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { AlertTriangle, FileText, Mail } from "lucide-react";
import Reveal from "../components/ui/Reveal";
import SEO, { generateBreadcrumbSchema, generateWebPageSchema } from "../components/seo/SEO";
import { getLatestNews, getNews, getNewsByTag, getTags, getTrendingNews } from "../lib/news";

const PLACEHOLDER_IMAGE = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='800' height='600' viewBox='0 0 800 600'%3E%3Cdefs%3E%3ClinearGradient id='grad' x1='0%25' y1='0%25' x2='100%25' y2='100%25'%3E%3Cstop offset='0%25' style='stop-color:%2343ad69;stop-opacity:0.1'/%3E%3Cstop offset='100%25' style='stop-color:%23094268;stop-opacity:0.1'/%3E%3C/linearGradient%3E%3C/defs%3E%3Crect fill='url(%23grad)' width='800' height='600'/%3E%3Ctext fill='%234a5d52' font-family='system-ui,sans-serif' font-size='24' font-weight='600' x='50%25' y='50%25' text-anchor='middle' dominant-baseline='middle'%3ECampusGenie%3C/text%3E%3C/svg%3E";

function formatDate(dateString) {
  if (!dateString) return "";
  return new Date(dateString).toLocaleDateString("en-NG", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function formatReadTime(content) {
  if (!content) return "3 min read";
  const words = content.split(/\s+/).length;
  const minutes = Math.ceil(words / 200);
  return `${minutes} min read`;
}

export default function BlogPage() {
  const tagScrollerRef = useRef(null);
  const [articles, setArticles] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [tags, setTags] = useState([]);
  const [selectedTag, setSelectedTag] = useState("All");
  const [meta, setMeta] = useState({ current_page: 1, last_page: 1 });
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadingFeatured, setLoadingFeatured] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearch(searchQuery.trim());
    }, 350);

    return () => window.clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    let active = true;

    async function loadFeatured() {
      setLoadingFeatured(true);
      try {
        const [trendingResponse, latestResponse] = await Promise.all([
          getTrendingNews(debouncedSearch).catch(() => ({ data: [] })),
          getLatestNews(debouncedSearch).catch(() => ({ data: [] })),
        ]);

        const trending = Array.isArray(trendingResponse.data)
          ? trendingResponse.data
          : trendingResponse.data?.data ?? [];
        const latest = Array.isArray(latestResponse.data)
          ? latestResponse.data
          : latestResponse.data?.data ?? [];

        const merged = [...trending, ...latest].filter(
          (item, index, array) => item?.id && array.findIndex((entry) => entry.id === item.id) === index
        );

        if (active) {
          setFeatured(merged.slice(0, 5));
        }
      } finally {
        if (active) {
          setLoadingFeatured(false);
        }
      }
    }

    async function loadTagsList() {
      try {
        const response = await getTags();
        const list = Array.isArray(response.data) ? response.data : response.data?.data ?? [];
        if (active) {
          setTags(list);
        }
      } catch {
        if (active) {
          setTags([]);
        }
      }
    }

    loadFeatured();
    loadTagsList();

    return () => {
      active = false;
    };
  }, [debouncedSearch]);

  useEffect(() => {
    let active = true;

    async function loadArticles(page = 1) {
      setLoading(true);
      setError("");

      try {
        const response =
          selectedTag === "All"
            ? await getNews(page, debouncedSearch)
            : await getNewsByTag(selectedTag, page, debouncedSearch);
        const list = Array.isArray(response.data) ? response.data : response.data?.data ?? [];
        const nextMeta = response.meta ?? { current_page: 1, last_page: 1 };

        if (active) {
          setArticles(list);
          setMeta(nextMeta);
        }
      } catch (requestError) {
        if (active) {
          setError(requestError.message || "Unable to load articles right now.");
          setArticles([]);
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    }

    loadArticles(1);

    return () => {
      active = false;
    };
  }, [selectedTag, debouncedSearch]);

  const visibleTags = [{ name: "All", count: null }, ...tags];

  async function loadArticlesPage(page) {
    setLoading(true);
    setError("");

    try {
      const response =
        selectedTag === "All"
          ? await getNews(page, debouncedSearch)
          : await getNewsByTag(selectedTag, page, debouncedSearch);
      const list = Array.isArray(response.data) ? response.data : response.data?.data ?? [];
      setArticles(list);
      setMeta(response.meta ?? { current_page: page, last_page: page });
    } catch (requestError) {
      setError(requestError.message || "Unable to load articles right now.");
      setArticles([]);
    } finally {
      setLoading(false);
    }
  }

  async function handlePageChange(page) {
    await loadArticlesPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  const articleCount = meta.total ?? articles.length;

  const heroArticle = featured[0];
  const sideArticles = featured.slice(1, 4);

  const breadcrumbs = [
    { name: "Home", url: "/" },
    { name: "Blog", url: "/blog" },
  ];

  const structuredData = [
    generateWebPageSchema({
      title: "Blog - CampusGenie",
      description: "JAMB tips, campus life guides, product updates, and success stories from students across Nigeria.",
      url: "/blog",
    }),
    generateBreadcrumbSchema(breadcrumbs),
  ];

  return (
    <>
      <SEO
        title="Blog - JAMB Tips, Campus Life & Student Success Stories"
        description="Get the latest JAMB preparation tips, WAEC study strategies, campus life guides, product updates, and success stories from students across Nigeria. Your guide to academic success."
        url="/blog"
        structuredData={structuredData}
      />

      {/* Hero Section */}
      <section className="blog-hero-section">
        <div className="container">
          <motion.div
            className="blog-hero-header"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="blog-hero-title">
              Insights, stories & updates from the student success platform.
            </h1>
            <p className="blog-hero-subtitle">
              JAMB tips, campus life guides, product updates, and success stories from students across Nigeria.
            </p>
            
            <div className="blog-search-container">
              <div className="blog-search-box">
                <svg className="blog-search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8" />
                  <path d="M21 21l-4.35-4.35" />
                </svg>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  placeholder="Search articles..."
                  className="blog-search-input"
                />
                {searchQuery && (
                  <button 
                    className="blog-search-clear"
                    onClick={() => setSearchQuery("")}
                  >
                    ×
                  </button>
                )}
              </div>
            </div>
          </motion.div>

          {/* Featured Articles */}
          {loadingFeatured ? (
            <div className="blog-featured-skeleton">
              <div className="skeleton-main" />
              <div className="skeleton-side">
                <div className="skeleton-card" />
                <div className="skeleton-card" />
                <div className="skeleton-card" />
              </div>
            </div>
          ) : heroArticle ? (
            <motion.div
              className="blog-featured-grid"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.2 }}
            >
              <Link to={`/blog/${heroArticle.id}`} className="blog-featured-main">
                <div className="blog-featured-main-image">
                  <img
                    src={heroArticle.image || PLACEHOLDER_IMAGE}
                    alt={heroArticle.title}
                    onError={(e) => { e.currentTarget.src = PLACEHOLDER_IMAGE; }}
                  />
                  <div className="blog-featured-main-overlay" />
                </div>
                <div className="blog-featured-main-content">
                  <div className="blog-featured-main-meta">
                    <span className="blog-category-badge">Featured</span>
                    <span className="blog-meta-dot">•</span>
                    <span>{formatDate(heroArticle.created_at)}</span>
                    <span className="blog-meta-dot">•</span>
                    <span>{formatReadTime(heroArticle.content)}</span>
                  </div>
                  <h2 className="blog-featured-main-title">{heroArticle.title}</h2>
                  <p className="blog-featured-main-excerpt">
                    {heroArticle.excerpt || "Discover the latest insights and updates from the CampusGenie team."}
                  </p>
                  <span className="blog-read-more">
                    Read Article
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M5 12h14M12 5l7 7-7 7" />
                    </svg>
                  </span>
                </div>
              </Link>

              <div className="blog-featured-side">
                {sideArticles.map((article, index) => (
                  <Link 
                    key={article.id} 
                    to={`/blog/${article.id}`} 
                    className="blog-featured-side-card"
                  >
                    <div className="blog-featured-side-image">
                      <img
                        src={article.image || PLACEHOLDER_IMAGE}
                        alt={article.title}
                        onError={(e) => { e.currentTarget.src = PLACEHOLDER_IMAGE; }}
                      />
                    </div>
                    <div className="blog-featured-side-content">
                      <span className="blog-featured-side-meta">
                        {formatDate(article.created_at)} • {formatReadTime(article.content)}
                      </span>
                      <h3 className="blog-featured-side-title">{article.title}</h3>
                    </div>
                  </Link>
                ))}
              </div>
            </motion.div>
          ) : null}
        </div>
      </section>

      {/* All Articles Section */}
      <section className="blog-articles-section">
        <div className="container">
          <Reveal>
            <div className="blog-section-header">
              <div className="blog-section-title-row">
                <h2 className="blog-section-title">All Articles</h2>
                <span className="blog-article-count">
                  {articleCount} article{articleCount !== 1 ? "s" : ""}
                </span>
              </div>
              
              <div className="blog-tags-container" ref={tagScrollerRef}>
                {visibleTags.map((tag) => (
                  <button
                    key={tag.name}
                    type="button"
                    className={`blog-tag-pill ${selectedTag === tag.name ? "active" : ""}`}
                    onClick={() => setSelectedTag(tag.name)}
                  >
                    {tag.name}
                    {typeof tag.count === "number" && <span className="blog-tag-count">{tag.count}</span>}
                  </button>
                ))}
              </div>
            </div>
          </Reveal>

          {error && (
            <div className="blog-error-state">
              <span className="blog-error-icon" aria-hidden>
                <AlertTriangle size={48} strokeWidth={1.75} />
              </span>
              <p>{error}</p>
              <button onClick={() => loadArticlesPage(1)} className="btn btn-secondary">
                Try Again
              </button>
            </div>
          )}

          {loading ? (
            <div className="blog-grid-skeleton">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="blog-card-skeleton">
                  <div className="skeleton-image" />
                  <div className="skeleton-content">
                    <div className="skeleton-line short" />
                    <div className="skeleton-line" />
                    <div className="skeleton-line" />
                  </div>
                </div>
              ))}
            </div>
          ) : articles.length > 0 ? (
            <div className="blog-articles-grid">
              {articles.map((article, index) => (
                <Reveal key={article.id} delay={index * 0.05}>
                  <Link to={`/blog/${article.id}`} className="blog-article-card">
                    <div className="blog-article-image">
                      <img
                        src={article.image || PLACEHOLDER_IMAGE}
                        alt={article.title}
                        onError={(e) => { e.currentTarget.src = PLACEHOLDER_IMAGE; }}
                      />
                      <div className="blog-article-image-overlay" />
                    </div>
                    <div className="blog-article-content">
                      <div className="blog-article-meta">
                        <span>{formatDate(article.created_at)}</span>
                        <span className="blog-meta-dot">•</span>
                        <span>{formatReadTime(article.content)}</span>
                      </div>
                      <h3 className="blog-article-title">{article.title}</h3>
                      <p className="blog-article-excerpt">
                        {article.excerpt || "Read this article to learn more about student success strategies."}
                      </p>
                      {article.tags?.length > 0 && (
                        <div className="blog-article-tags">
                          {article.tags.slice(0, 2).map((tag) => (
                            <span key={tag} className="blog-article-tag">{tag}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          ) : (
            <div className="blog-empty-state">
              <span className="blog-empty-icon" aria-hidden>
                <FileText size={48} strokeWidth={1.75} />
              </span>
              <h3>No articles found</h3>
              <p>
                {debouncedSearch
                  ? `No articles matched "${debouncedSearch}". Try a different search or tag.`
                  : "Try selecting a different tag to find what you're looking for."}
              </p>
              <button
                onClick={() => {
                  setSearchQuery("");
                  setSelectedTag("All");
                }}
                className="btn btn-secondary"
              >
                Clear Filters
              </button>
            </div>
          )}

          {!loading && !error && meta.last_page > 1 && (
            <Reveal>
              <div className="blog-pagination">
                <button
                  type="button"
                  onClick={() => handlePageChange(meta.current_page - 1)}
                  disabled={meta.current_page <= 1}
                  className="blog-pagination-btn"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M19 12H5M12 19l-7-7 7-7" />
                  </svg>
                  Previous
                </button>
                <div className="blog-pagination-info">
                  <span className="blog-pagination-current">{meta.current_page}</span>
                  <span className="blog-pagination-separator">/</span>
                  <span className="blog-pagination-total">{meta.last_page}</span>
                </div>
                <button
                  type="button"
                  onClick={() => handlePageChange(meta.current_page + 1)}
                  disabled={meta.current_page >= meta.last_page}
                  className="blog-pagination-btn"
                >
                  Next
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M5 12h14M12 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            </Reveal>
          )}
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="blog-newsletter-section">
        <div className="container">
          <Reveal>
            <div className="blog-newsletter-card">
              <div className="blog-newsletter-content">
                <span className="blog-newsletter-icon" aria-hidden>
                  <Mail size={40} strokeWidth={1.75} />
                </span>
                <h3>Stay in the loop</h3>
                <p>Get the latest JAMB tips, campus updates, and success stories delivered to your inbox.</p>
              </div>
              <div className="blog-newsletter-form">
                <input type="email" placeholder="Enter your email" className="blog-newsletter-input" />
                <button className="btn btn-primary">Subscribe</button>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
